// Use Vite to bundle and serve the pdf.js worker locally (no CDN)
// This resolves to a URL emitted by the bundler

// eslint-disable-next-line import/default
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'
import { useEffect, useRef } from 'react'

import { fitPdfRenderScale } from './pdfRenderScale'

interface PdfViewerProps {
  pdfUrl: string
  scale?: number
  onPagesConverted?: (pngDataUrls: string[]) => void
  onError?: (error: Error) => void
  onProgress?: (current: number, total: number) => void
}

// pdfjs-dist v5 loads several runtime assets on demand:
//   - openjpeg.wasm  -> JPEG 2000 image decode (JPX)
//   - jbig2.wasm     -> JBIG2 image decode
//   - qcms_bg.wasm   -> color management
//   - cmaps/*        -> CJK / non-Latin glyph maps
//   - standard_fonts -> PDF "standard 14" fonts
// These are emitted under /pdfjs/* by `pdfjsAssetsPlugin` (see vite.config.ts).
// Without `wasmUrl`, JPX-bearing PDFs fail with `JpxError: OpenJPEG failed to initialize`.
function getPdfjsAssetUrl(subdir: 'wasm' | 'cmaps' | 'standard_fonts'): string {
  const base = getBasePathFromConfig()
  const prefix = base ? `/${base}` : ''
  return `${prefix}/pdfjs/${subdir}/`
}

function getBasePathFromConfig(): string {
  if (typeof document === 'undefined') return ''
  try {
    const el = document.getElementById('config-vars-embed')
    if (!el?.textContent) return ''
    const config = JSON.parse(el.textContent) as { BASE_PATH?: string }
    return typeof config.BASE_PATH === 'string' ? config.BASE_PATH : ''
  } catch {
    return ''
  }
}

export async function convertPdfToPngsConverter(
  pdfUrl: string,
  options?: {
    scale?: number
    /** Per-page rotation overrides (1-indexed string keys). Overrides the PDF's native page rotation. */
    pageRotations?: Record<string, number>
    onProgress?: (current: number, total: number) => void
  }
): Promise<string[]> {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Could not get canvas context')
  }

  try {
    // Dynamically import pdfjs to avoid loading browser-only APIs during test environments
    const pdfjsLib = await import('pdfjs-dist')

    // Use bundled worker asset (no CDN). All runtime assets resolve via
    // BASE_PATH so this works in both local dev and deployed environments
    // that mount the app under a sub-path.
    const STANDARD_FONTS_PATH = getPdfjsAssetUrl('standard_fonts')
    const CMAP_URL = getPdfjsAssetUrl('cmaps')
    const WASM_URL = getPdfjsAssetUrl('wasm')

    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      standardFontDataUrl: STANDARD_FONTS_PATH,
      cMapUrl: CMAP_URL,
      cMapPacked: true,
      wasmUrl: WASM_URL,
      fontExtraProperties: true,
    })

    const pdf = await loadingTask.promise
    const totalPages = pdf.numPages
    const pngDataUrls: string[] = []

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
      try {
        const page = await pdf.getPage(pageNumber)
        const pageRotation = options?.pageRotations?.[String(pageNumber)]
        const scale = fitPdfRenderScale(
          page.getViewport({ scale: 1 }),
          options?.scale || 2
        )
        const viewport = page.getViewport({ scale, rotation: pageRotation })

        canvas.width = viewport.width
        canvas.height = viewport.height

        await page.render({
          canvasContext: context,
          canvas: canvas,
          viewport,
        }).promise

        const pngDataUrl = canvas.toDataURL('image/png')
        if (pngDataUrl === 'data:,') {
          throw new Error(
            `Page ${pageNumber} rendered empty at ${canvas.width}×${canvas.height} px`
          )
        }
        pngDataUrls.push(pngDataUrl)

        options?.onProgress?.(pageNumber, totalPages)
      } catch (error) {
        console.error(
          `[PdfToPngConverter] Error rendering page ${pageNumber}:`,
          error,
          {
            pdfUrl,
            pageNumber,
            totalPages,
            errorName: (error as Error)?.name,
            errorMessage: (error as Error)?.message,
          }
        )
        throw error
      }
    }

    return pngDataUrls
  } catch (error) {
    console.error('[PdfToPngConverter] Error converting PDF to PNGs:', error, {
      pdfUrl,
      errorName: (error as Error)?.name,
      errorMessage: (error as Error)?.message,
    })
    throw error
  } finally {
    canvas.width = 0
    canvas.height = 0
  }
}

export function PdfToPngConverter({
  pdfUrl,
  scale = 2,
  onPagesConverted,
  onError,
  onProgress,
}: PdfViewerProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const convertPdf = async () => {
      try {
        const pngDataUrls = await convertPdfToPngsConverter(pdfUrl, {
          scale,
          onProgress,
        })
        onPagesConverted?.(pngDataUrls)
      } catch (error) {
        console.error('Error in PdfViewer:', error)
        onError?.(error as Error)
      }
    }

    convertPdf()
  }, [pdfUrl, scale, onPagesConverted, onError, onProgress])

  return <canvas ref={canvasRef} style={{ display: 'none' }} />
}
